'use client'

import { useState, useRef, useEffect, useCallback } from 'react'
import { datadogLogs } from '@datadog/browser-logs'
import { fromInteger } from '@repo/money'
import InfoCircleIcon from 'src/app/assets/icons/ico-info-circle.svg'
import LightningIcon from 'src/app/assets/icons/ico-bolt-lightning.svg'
import { type MarbleGame } from 'src/types/domain/Game'
import { useTimer } from 'src/hooks/useTimer'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage, useGetGameBetConfig, useGetGameConfig } from 'src/store/game'
import { isBirdieGameType, isCrashGameType } from 'src/utils/gameTypeUtils'
import { birdieRtpRow } from 'src/components/RightSidebar/components/birdieRtpRow'
import { selectRoundOdds } from 'src/components/BirdieRoundCard/roundOdds'
import { getRaceRoundId } from 'src/utils/getRaceRoundId'
import { useSoundEffect } from 'src/hooks/useSoundEffect'
import { useUIActions } from 'src/store/ui'
import { useGetCurrentPlayer, useGetCurrentPlayerId } from 'src/store/player'
import { displayAmount, displayMoney } from 'src/utils/displayAmount'
import { isAuthenticatedPlayer } from 'src/types/domain/Player'
import { getCurrencySymbol } from 'src/utils/getCurrencySymbol'
import { getEffectiveBetStats } from 'src/utils/gameConfigStats'
import { resolveGameKey } from 'src/utils/effectiveRtp'
import { isKnownGameConfig } from 'src/types/domain/GameConfig'
import { getTranslatedGameTitle } from 'src/services/i18n/utils/gameTranslations'
import { getLatestEvent } from 'src/utils/raceUtils'
import { isRaceStartedEvent } from 'src/types/domain/RaceEvent'
import { isCCTVGameType } from 'src/utils/gameTypeUtils'
import ArrowLeftIcon from 'src/app/assets/icons/ico-arrow-left.svg'
import One55SymbolWhiteSimple from 'src/app/assets/icons/ico-155-symbol-simple.svg'
import { ClearButton } from '../../ClearButton'
import { StreakIndicator } from '../../StreakIndicator'
import { useGetCurrentStreak } from 'src/store/streak'

type Props = Readonly<{
  game: MarbleGame
}>

export function ToolbarInfo({ game }: Props) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  const { playEffect } = useSoundEffect()
  const gameConfig = useGetGameConfig()
  const { lobbyDisabled } = gameConfig
  const isChipPlacement = 'bettingMode' in gameConfig && gameConfig.bettingMode === 'chip-placement'
  const { tracksPanelOpened } = useUIActions()
  const [isToolbarInfoDropdownActive, setIsToolbarInfoDropdownActive] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const currentRace = game.races[0]
  const { eventInfo } = useTimer({
    race: currentRace,
    closingSeconds: game.bettingClosingWindow || undefined
  })
  const currentPlayerId = useGetCurrentPlayerId()
  const player = useGetCurrentPlayer()
  const betConfig = useGetGameBetConfig(player)
  const translatedTitle = getTranslatedGameTitle(game.track, t)
  const isIrlAltGame =
    game.track.gameType === 'irl_alt' ||
    game.track.gameType === 'duck_river' ||
    game.track.gameType === 'snow_run' ||
    game.track.gameType === 'social_betting' ||
    game.track.gameType === 'crash'
  // v47: the CCTV prefix goes through i18n (it rendered hardcoded English
  // in the toolbar title on all 14 non-English locales).
  const displayTitle = isIrlAltGame
    ? t('CCTV Game - {{title}}', { title: translatedTitle })
    : translatedTitle

  // Streak state
  const currentStreak = useGetCurrentStreak()

  // Lightning round state. In chip-placement mode the lightning info
  // lives entirely on the chip-placement panel itself (top-left
  // boost indicator + bottom-centre Pays multiplier ticking up at
  // reveal), so the toolbar overlay would just duplicate the same
  // information in a less impactful spot — suppress it entirely.
  // Classic mode keeps the toolbar label as the primary surface.
  const latestEvent = getLatestEvent(currentRace.raceEvents)

  // CCTV-only: location for the active round, surfaced in the
  // toolbar once race_started fires (betting closed → counting).
  // The engine attaches `location` to the race_started payload
  // alongside boundaries + optionLabels, so this is reliable as
  // soon as the realtime event arrives. Absent for non-CCTV games
  // and pre-deploy CCTV rounds.
  const cctvLocation = (() => {
    // Crash is grouped with CCTV for music-suppression only — it is NOT a
    // counting/boundary game, so it must never surface the counting-round
    // location pill even if its clip payload carries streamMetadata.
    if (!isCCTVGameType(game.track.gameType) || isCrashGameType(game.track.gameType)) return null
    const startedEvent = currentRace.raceEvents.find(isRaceStartedEvent)
    return startedEvent?.payload?.location ?? null
  })()
  const isLightningRound =
    !isChipPlacement && currentRace.isLightningRound && currentRace.lightningBoostType
  const isExpanded =
    isLightningRound &&
    latestEvent?.eventType !== 'race_started' &&
    latestEvent?.eventType !== 'race_ended' &&
    latestEvent?.eventType !== 'race_crashed'

  // Get boosted marble colors for expanded label
  const boostedMarbleColors =
    isLightningRound && currentRace.lightningBoostedItems
      ? currentRace.lightningBoostedItems
          .map((id) => game.marbles.find((m) => m.uuid === id)?.color)
          .filter(Boolean)
          .map((color) => color!.charAt(0).toUpperCase() + color!.slice(1))
      : []

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        isToolbarInfoDropdownActive
      ) {
        setIsToolbarInfoDropdownActive(false)
        playEffect('click')
      }
    },
    [isToolbarInfoDropdownActive, playEffect]
  )

  useEffect(() => {
    if (isToolbarInfoDropdownActive) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isToolbarInfoDropdownActive, handleClickOutside])

  // While the Track Info dropdown is open, flag the body so CSS can
  // drop the mobile `.marbles-race__bottom` (z-index 900) below the
  // toolbar so the dropdown isn't painted over on small screens
  // (QA-60161).
  useEffect(() => {
    if (!isToolbarInfoDropdownActive) return
    document.body.classList.add('track-info-open')
    return () => document.body.classList.remove('track-info-open')
  }, [isToolbarInfoDropdownActive])

  const toggleToolbarInfoDropdown = () => {
    setIsToolbarInfoDropdownActive(!isToolbarInfoDropdownActive)
    playEffect('click')
  }

  const handleBackBtnClick = () => {
    tracksPanelOpened(true)
    playEffect('click')

    datadogLogs.logger.info('tracks_panel', {
      user_action: {
        type: 'open',
        currentGame: game.id,
        playerId: currentPlayerId
      }
    })
  }

  if (!isAuthenticatedPlayer(player)) return null
  const currentCurrency = getCurrencySymbol(player.balance.currency)
  const logoClasses = 'game-toolbar-btn back-btn'

  return (
    <div className="game-top-toolbar__left">
      <div className="toolbar-top-row">
        {!lobbyDisabled && (
          <ClearButton className={logoClasses} onClick={handleBackBtnClick}>
            <ArrowLeftIcon />
            <One55SymbolWhiteSimple />
          </ClearButton>
        )}

        <div className="toolbar-info" ref={dropdownRef}>
          <ClearButton
            className={`toolbar-info__button ${isToolbarInfoDropdownActive ? 'active' : ''}`}
            onClick={toggleToolbarInfoDropdown}
          >
            {Boolean(lobbyDisabled) && <One55SymbolWhiteSimple />}
            <div className="toolbar-info__button__inner">
              <span className="track-name">
                {displayTitle} <InfoCircleIcon />
              </span>
              <span className="track-race-round">
                {getRaceRoundId(currentRace.round, game.track.shortName)}
                {cctvLocation ? (
                  <span className="track-race-round__location">{cctvLocation}</span>
                ) : null}
              </span>
            </div>
          </ClearButton>
          {isToolbarInfoDropdownActive ? (
            <div className="toolbar-info__dropdown">
              <div className="toolbar-info__dropdown__inner">
                <div className="toolbar-info__dropdown__header">
                  <span>{t('Game Info')}</span>
                </div>
                <div className="toolbar-info__dropdown__content">
                  <div className="entry">
                    <span>{t('Game')}:</span>
                    <span>{displayTitle}</span>
                  </div>

                  <div className="entry">
                    <span>{t('Round ID')}:</span>
                    <span>
                      #{game.races[0].round}
                      {/* Crash: no inline phase/timer — the hero narrates the
                          round, the race-genre labels read wrong here, and
                          the raw event clock flips phases ~2s ahead of the
                          viewer's frames (anti-tell). Other games keep it,
                          gated on a non-empty time so it never renders as
                          "#282 ( · Race Running)". */}
                      {(() => {
                        if (!eventInfo || isCrashGameType(game.track.gameType)) return null
                        const time = eventInfo.getTime()
                        if (!time) return null
                        return ` (${time}${eventInfo.label ? ` · ${eventInfo.label}` : ''})`
                      })()}
                    </span>
                  </div>

                  {betConfig && isAuthenticatedPlayer(player) ? (
                    <>
                      <div className="entry">
                        <span>{t('Min bet amount')}:</span>
                        <span>
                          {currentCurrency}{' '}
                          {(() => {
                            const v = fromInteger(betConfig.minBetAmount, player.balance.currency)
                            return displayMoney(v, true)
                          })()}
                        </span>
                      </div>

                      <div className="entry">
                        <span>{t('Max bet amount')}:</span>
                        <span>
                          {currentCurrency}{' '}
                          {(() => {
                            const v = fromInteger(betConfig.maxBetAmount, player.balance.currency)
                            return displayMoney(v, true)
                          })()}
                        </span>
                      </div>

                      {/* Hide max multiplier for crash games - the betConfig.maxMultiplier is for marble racing, not crash */}
                      {!isCrashGameType(game.track.gameType) && (
                        <div className="entry">
                          <span>{t('Max multiplier')}:</span>
                          {/* For chip-placement, betConfig.maxMultiplier is 0
                              (per-market values live on `markets[]`) so we
                              derive the effective max via `getEffectiveBetStats`
                              — also folds in the legendary 5x lightning factor
                              so the headline matches what the player can
                              actually see on a lucky round. */}
                          <span>
                            {(() => {
                              // Birdie has no static price: every option is
                              // repriced per round from the golfer's make rate,
                              // so the config's market multipliers say nothing.
                              // Same source and same words as the sidebar's RTP
                              // row — this round's highest price, flagged as
                              // varying — so the two surfaces can never disagree.
                              if (isBirdieGameType(game.track.gameType)) {
                                return birdieRtpRow(selectRoundOdds(currentRace), t).maxPayout
                              }
                              if (!isKnownGameConfig(gameConfig)) {
                                return `${displayAmount(betConfig.maxMultiplier)}x`
                              }
                              // Pass game shape so chip-placement games
                              // that can host forecast/tricast side-bets
                              // show the side-bet ceiling (e.g. 285.6x
                              // tricast on 8 marbles) instead of just
                              // the main-market PickWinner max (~7x).
                              const stats = getEffectiveBetStats(
                                gameConfig,
                                {
                                  totalMarbles: game.totalMarbles,
                                  maxPickableMarbles: game.maxPickableMarbles,
                                  resultsToSubmit: game.resultsToSubmit
                                },
                                // Drives both the game's own side-bet
                                // margins and the legendary-lightning
                                // ceiling, which only applies to games
                                // that actually have Lightning.
                                resolveGameKey(game.id)
                              )
                              return `${displayAmount(stats.maxMultiplier)}x`
                            })()}
                          </span>
                        </div>
                      )}

                      <div className="entry">
                        <span>{t('Max payout')}:</span>
                        <span>
                          {currentCurrency}{' '}
                          {(() => {
                            const v = fromInteger(betConfig.maxPayout, player.balance.currency)
                            return displayMoney(v, true)
                          })()}
                        </span>
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {isLightningRound && currentRace.lightningBoostType && (
        <div
          className={`lightning-label expanded lightning-label--${currentRace.lightningBoostType}`}
        >
          <div className="lightning-label__title">
            <LightningIcon />
            <span>{t('Lightning Round')}</span>
          </div>
          {currentRace.lightningBoostPercent && (
            <div className="lightning-label__details">
              {t('+{{percent}}% Boost on {{colors}}', {
                percent: currentRace.lightningBoostPercent,
                colors: boostedMarbleColors.join(', ')
              })}
            </div>
          )}
        </div>
      )}

      {currentStreak && (
        <StreakIndicator
          streakType={currentStreak.streakType}
          streakCount={currentStreak.streakCount}
          boostPercent={currentStreak.streakBoostPercent}
        />
      )}
    </div>
  )
}
